import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { emailSchema } from '@/lib/validations'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { useMe } from '@/hooks/useMe'
import { useMyPermissions } from '@/hooks/useMyPermissions'
import { AllowedAction } from '@/data/allowedActions'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { adSupplierUsersService } from '@/services/ad_supplier_users_service'
import { useAuth } from '@/hooks/useAuth'
import { useUserRoles } from '@/hooks/useUserRoles'
import { toast } from 'sonner'

const formSchema = z.object({
  account: z.string().min(1, { message: '請輸入使用者帳號' }),
  user_name: z.string().min(1, { message: '請輸入使用者名稱' }),
  email: emailSchema(),
  user_role_id: z.string().optional(),
  is_super_admin: z.boolean(),
})

type FormValues = z.infer<typeof formSchema>

interface AdSupplierUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  supplierId: number
  onSuccess?: () => void
}

export function AdSupplierUserDialog({
  open,
  onOpenChange,
  supplierId,
  onSuccess,
}: AdSupplierUserDialogProps) {
  const { token } = useAuth()
  const { user: me } = useMe()
  const { hasPermission } = useMyPermissions()
  // 超級管理員只有管理端（sys_admin／組織）能指定
  const canSetSuperAdmin =
    me?.role_type === 'sys_admin' ||
    hasPermission('system_provider', AllowedAction.C) ||
    hasPermission('system_ad_supplier', AllowedAction.C) ||
    hasPermission('provider_supplier_management', AllowedAction.C)

  // Fetch ad_supplier-level roles for this supplier
  const { userRoles, isLoading: isLoadingRoles } = useUserRoles({
    pageSize: 100,
    scopeType: 'ad_supplier',
    supplierId,
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      account: '',
      user_name: '',
      email: '',
      user_role_id: '',
      is_super_admin: false,
    },
  })

  const onSubmit = async (values: FormValues) => {
    if (!token) return

    const result = await adSupplierUsersService.createNewUserAndAddToSupplier(
      {
        supplier_id: supplierId,
        account: values.account,
        user_name: values.user_name,
        email: values.email,
        user_role_id: values.user_role_id ? parseInt(values.user_role_id, 10) : undefined,
        ...(canSetSuperAdmin && values.is_super_admin ? { is_super_admin: true } : {}),
      },
      token,
    )

    if (result.success) {
      toast.success('新增使用者與供應商關聯成功')
      form.reset()
      onOpenChange(false)
      onSuccess?.()
    } else {
      toast.error(result.error?.message || '帳號可能已存在')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>新增供應商使用者</DialogTitle>
          <DialogDescription>新增使用者至供應商</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者帳號</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入使用者帳號" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入使用者名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="example@domain.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_role_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>角色 (選填)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={isLoadingRoles ? '載入中...' : '請選擇角色'} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {userRoles.map((role) => (
                        <SelectItem key={role.user_role_id} value={role.user_role_id.toString()}>
                          {role.role_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {canSetSuperAdmin && (
              <FormField
                control={form.control}
                name="is_super_admin"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>設為超級管理員</FormLabel>
                      <p className="text-xs text-muted-foreground">對這家廣告商所有功能全開，無視角色；可指定多個。</p>
                    </div>
                  </FormItem>
                )}
              />
            )}
            <DialogFooter>
              <Button type="submit">新增</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
